<template>
  <div class="backdrop" aria-hidden="true">
    <div class="backdrop__grid" :style="gridStyle"></div>
    <div class="backdrop__beam" :style="beamStyle"></div>
    <div class="backdrop__vignette"></div>
    <svg class="backdrop__noise" xmlns="http://www.w3.org/2000/svg">
      <filter id="bd-grain">
        <feTurbulence type="fractalNoise" baseFrequency="0.82" numOctaves="3" stitchTiles="stitch" />
        <feColorMatrix type="saturate" values="0" />
      </filter>
      <rect width="100%" height="100%" filter="url(#bd-grain)" />
    </svg>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useViewport } from '../../composables/useViewport'

const vp = useViewport()

/* The grid drifts slowly upward — the classic "deep background" parallax rate. */
const gridStyle = computed(() => ({
  transform: `translate3d(${vp.pointerX * -10}px, ${-vp.scrollY * 0.035}px, 0)`
}))

/* A soft key light that tracks scroll position, so the page itself is "lit"
   from a moving source — the same idea the lessons teach about shadows. */
const beamStyle = computed(() => ({
  transform: `translate3d(${vp.pointerX * 40}px, ${vp.progress * 40 - 20}vh, 0)`,
  opacity: 0.5 + vp.progress * 0.25
}))
</script>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background:
    radial-gradient(120% 80% at 50% -10%, #0d1520 0%, var(--ink-900) 62%);
}

.backdrop__grid {
  position: absolute;
  inset: -20% -10%;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(rgba(255, 255, 255, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px);
  background-size: 28px 28px, 28px 28px, 140px 140px, 140px 140px;
  mask-image: radial-gradient(90% 70% at 50% 30%, #000 0%, transparent 78%);
  -webkit-mask-image: radial-gradient(90% 70% at 50% 30%, #000 0%, transparent 78%);
  will-change: transform;
}

.backdrop__beam {
  position: absolute;
  left: 50%;
  top: -30vh;
  width: 120vw;
  height: 120vh;
  margin-left: -60vw;
  background:
    radial-gradient(closest-side, rgba(255, 180, 92, 0.09), transparent 70%),
    radial-gradient(closest-side at 70% 60%, rgba(86, 225, 240, 0.07), transparent 72%);
  filter: blur(20px);
  will-change: transform, opacity;
}

.backdrop__vignette {
  position: absolute;
  inset: 0;
  background: radial-gradient(120% 90% at 50% 45%, transparent 42%, rgba(0, 0, 0, 0.72) 100%);
}

.backdrop__noise {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.035;
  mix-blend-mode: overlay;
}
</style>
